import { ChevronDown, Plus, X } from 'lucide-react';
import React, { useCallback, useContext, useEffect, useState } from 'react';

import cx from 'classnames';
import { AssignmentTaskValueTypes } from 'constants/assignments';
import { Formik } from 'formik';
import { chain, every, isNil } from 'lodash';
import {
  createTrackerFromAssignmentTemplate,
  useAssignmentTemplate
} from 'sdk';
import { mathAssignmentCreateViaCopy } from 'sdk';

import TeacherAssignmentCreate from 'pages/Teachers/Assignments/Create';
import TeacherAssignmentCreateV2 from 'pages/Teachers/Assignments/CreateV2';
import { AssignmentDialogModes } from 'pages/Teachers/shared/AssignmentDialog';
import { getValidationSchema } from 'pages/Teachers/shared/AssignmentDialog/components/AssignAssignment/utils.js';
import Stepper from 'pages/Teachers/shared/AssignmentDialog/components/Stepper';
import {
  assignmentDetail,
  trackerCreate,
  trackerUpdate
} from 'pages/Teachers/shared/AssignmentDialog/sdk';
import AttachmentDropzone from 'pages/Teachers/shared/AttachmentDropzone';
import AttachmentList from 'pages/Teachers/shared/AttachmentList';
import AttachmentUploadOverlay from 'pages/Teachers/shared/AttachmentUploadOverlay';
import { colors } from 'theme/palette';
import {
  buildStepperFormDataForCreate,
  buildStepperFormDataForUpdate
} from 'utils/assignments';
import { setFormErrors } from 'utils/forms';
import { useDialog } from 'utils/hooks';
import { trackMixpanelEvent } from 'utils/integrations/mixpanel';
import {
  MixpanelAction,
  MixpanelObject
} from 'utils/integrations/mixpanel/constants';
import { notifyErrors } from 'utils/notifications';

import AssignmentDialogContext from 'components/AssignmentDialogContext';
import Dialog from 'components/Dialog';
import StandardDialogV2 from 'components/StandardDialogV2';
import Typography from 'components/Typography';
import UserContext from 'components/UserContext';

import { VALUES_OPTIONS } from '../../constants';
import { ReactComponent as UploadIcon } from './assets/upload.svg';
import styles from './styles.module.scss';

const FIRST_PAGE = {
  standards: [],
  count: 1
};

const ELA_INSIGHTS_ENABLED =
  process.env.REACT_APP_ELA_INSIGHTS_ENABLED === 'true';

const StepperAssignmentCreateForm = ({
  trackerId,
  mode = AssignmentDialogModes.CREATE,
  onCreateSuccess,
  onUpdateSuccess,
  defaultAssignmentName = '',
  assignmentTemplateId,
  isAcceleratedCurriculum,
  onClose,
  onCopySuccess,
  isMathAssignment
}) => {
  const [isWide, setIsWide] = useState(false);
  const [initialValues, setInitialValues] = useState(null);
  const [isFromTemplate, setIsFromTemplate] = useState(false);
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(isMathAssignment);
  const [activeSettingsTab, setActiveSettingsTab] = useState('attachments');

  const {
    isOpened: isStandardDialogOpened,
    closeDialog: closeStandardDialog,
    openDialog: openStandardDialog
  } = useDialog();

  const { isAssignmentDialogOpened, setIsAssignmentDialogOpened } = useContext(
    AssignmentDialogContext
  );

  const templateResult = useAssignmentTemplate({
    assignmentTemplateId: assignmentTemplateId || null,
    params: { is_accelerated: isAcceleratedCurriculum }
  });

  const { data: assignmentTemplate, isLoading: isTemplateLoading } =
    templateResult || {};

  const effectiveTemplateData = assignmentTemplateId
    ? assignmentTemplate
    : null;
  const effectiveLoading = assignmentTemplateId ? isTemplateLoading : false;

  const { user } = useContext(UserContext);

  const fetchAssignmentDetail = async ({ trackerId }) => {
    const { data, success, errors } = await assignmentDetail(trackerId);
    if (success) {
      return data;
    }
    notifyErrors(errors);
    return null;
  };

  const inNewCopyMode = mode === AssignmentDialogModes.COPY && isMathAssignment;

  const initialize = useCallback(async () => {
    let newInitialValues = {
      assignmentName: defaultAssignmentName,
      description: '',
      tasks: [FIRST_PAGE],
      rubric: AssignmentTaskValueTypes.RUBRIC_ZERO_FOUR,
      maxValue: 100,
      pageType: '1',
      pageCount: '1',
      attachments: []
    };

    const mapAssignmentData = (assignment) => {
      const taskCount = assignment.tasks?.length || 0;
      const pageType = taskCount <= 4 ? String(taskCount) : 'custom';
      const pageCount = String(taskCount);
      const valueType =
        assignment.tasks?.[0]?.value_type ||
        AssignmentTaskValueTypes.RUBRIC_ZERO_FOUR;
      const isTemplateData = 'title' in assignment;

      return {
        assignmentName: isTemplateData
          ? assignment.title
          : assignment.name || '',
        description: assignment.description || '',
        tasks: assignment.tasks || FIRST_PAGE,
        rubric: valueType,
        maxValue: 100,
        pageType: pageType,
        pageCount: pageCount,
        attachments: assignment.attachments || []
      };
    };

    if (assignmentTemplateId && effectiveTemplateData) {
      setIsFromTemplate(true);
      newInitialValues = {
        ...mapAssignmentData(effectiveTemplateData),
        isAcceleratedCurriculum
      };
    } else if (trackerId) {
      try {
        const assignment = await fetchAssignmentDetail({ trackerId });
        if (assignment) {
          const isCopyMode = mode === AssignmentDialogModes.COPY;

          if (!isNil(assignment.assignment_template_id)) {
            setIsFromTemplate(true);
          }

          const mappedValues = mapAssignmentData(assignment);
          newInitialValues = {
            ...mappedValues,
            assignmentName: isCopyMode
              ? `Copy of ${mappedValues.assignmentName}`
              : mappedValues.assignmentName
          };
        }
      } catch (error) {
        console.error('Error fetching assignment:', error);
        notifyErrors(error);
      }
    }

    setInitialValues(newInitialValues);
  }, [
    trackerId,
    mode,
    defaultAssignmentName,
    assignmentTemplateId,
    effectiveTemplateData,
    isAcceleratedCurriculum
  ]);

  const createTracker = useCallback(
    async ({ formData, onError }) => {
      try {
        if (assignmentTemplateId) {
          const { success, errors, data } =
            await createTrackerFromAssignmentTemplate({
              assignmentTemplateId,
              data: formData
            });

          if (success) {
            await onCreateSuccess(data);
          } else {
            onError(errors);
          }

          return success;
        }

        const { success, errors, data } = await trackerCreate(formData);

        if (success) {
          await onCreateSuccess(data);
        } else {
          onError(errors);
        }

        return success;
      } catch (error) {
        onError(error);
        return false;
      }
    },
    [onCreateSuccess, assignmentTemplateId]
  );

  const updateTracker = useCallback(
    async ({ formData, onError }) => {
      try {
        const { success, errors } = await trackerUpdate({
          trackerId,
          data: formData
        });

        if (success) {
          await onUpdateSuccess();
        } else {
          onError(errors);
        }

        return success;
      } catch (error) {
        onError(error);
        return false;
      }
    },
    [trackerId, onUpdateSuccess]
  );

  const onStandardsSelected = ({
    selectedStandards,
    formikTasks,
    setFieldValue
  }) => {
    formikTasks.forEach((task, taskIndex) => {
      const objectiveStandardsFieldName = `tasks.${taskIndex}.standards`;
      setFieldValue(objectiveStandardsFieldName, selectedStandards);
    });
    closeStandardDialog();
  };

  const onSubmit = async (values, { setFieldError, setSubmitting }) => {
    try {
      setSubmitAttempted(true);
      setSubmitting(true);

      const formData =
        mode === AssignmentDialogModes.UPDATE
          ? buildStepperFormDataForUpdate({ values })
          : buildStepperFormDataForCreate({ values });

      const onError = (errors) => setFormErrors(errors, setFieldError);

      if (inNewCopyMode) {
        const { success, errors, data } = await mathAssignmentCreateViaCopy({
          trackerId: trackerId,
          data: formData
        });
        if (success) {
          await onCopySuccess(data);
        } else {
          onError(errors);
          return false;
        }

        return success;
      }

      if (mode === AssignmentDialogModes.UPDATE) {
        return await updateTracker({ formData, onError });
      }

      return await createTracker({ formData, onError });
    } catch (error) {
      console.error('Form submission error:', error);
      setFieldError(
        'submit',
        'An unexpected error occurred. Please try again.'
      );
      return false;
    } finally {
      trackMixpanelEvent(
        user.id,
        `${MixpanelObject.FORM} ${MixpanelAction.SUBMIT}`,
        {
          feature: 'create',
          view: 'create',
          form: 'create',
          pages: values.pageCount,
          rubric: values.rubric,
          has_qa: false // This will be updated when we add questions and answers in the advanced tab.
        }
      );
      setSubmitting(false);
    }
  };

  useEffect(() => {
    if (!initialValues && (!assignmentTemplateId || !effectiveLoading)) {
      initialize();
    }
  }, [initialValues, initialize, assignmentTemplateId, effectiveLoading]);

  if (!initialValues || effectiveLoading) return null;

  const attachmentsText = isMathAssignment
    ? 'Attach an exemplar for the connected assignment.'
    : 'Attach any materials connected to the assignment.';

  const handleBackButtonClick = () => {
    setIsAssignmentDialogOpened(true);
    onClose();
  };

  const schema = getValidationSchema({
    isMathAssignment
  });

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={schema}
      onSubmit={onSubmit}
      enableReinitialize
    >
      {({
        values,
        errors,
        touched,
        setFieldValue,
        handleChange,
        handleSubmit,
        isSubmitting
      }) => {
        const showError = (fieldName) => submitAttempted && errors[fieldName];
        const assignmentStandards = chain(values.tasks)
          .map('standards')
          .flatten()
          .uniqBy('id')
          .value()
          .filter((standard) => standard !== undefined);

        const handleNextClick = () => {
          setSubmitAttempted(true);
          handleSubmit();
        };

        const nextButtonText = isMathAssignment
          ? inNewCopyMode
            ? 'Next: Assign →'
            : 'Next: Confirm →'
          : 'Next: Assign →';

        return (
          <AttachmentDropzone
            mode={mode}
            setFieldValue={setFieldValue}
            initialAttachments={values.attachments}
            disabled={inNewCopyMode}
          >
            {({ attachments, onRemove, isDragActive, openFileDialog }) => (
              <>
                {isDragActive && <AttachmentUploadOverlay />}
                <div className={styles.container}>
                  <Stepper
                    nextButtonText={nextButtonText}
                    backButtonText={'← BACK: CHOOSE TYPE'}
                    onNextClick={handleNextClick}
                    activeTab={'create'}
                    disableNextButton={
                      (mode !== AssignmentDialogModes.COPY &&
                        isMathAssignment &&
                        (values.attachments.length === 0 ||
                          !every(attachments, 'uploaded') ||
                          values.assignmentName === '' ||
                          assignmentStandards.length === 0)) ||
                      (values.attachments.length !== 0 &&
                        !every(attachments, 'uploaded')) ||
                      isSubmitting
                    }
                    onBackClick={handleBackButtonClick}
                    disableConfirm={
                      mode === AssignmentDialogModes.COPY || !isMathAssignment
                    }
                  />

                  {errors.submit && (
                    <div className={styles.errorBanner}>{errors.submit}</div>
                  )}

                  <div
                    className={`${styles.formGroup} ${
                      showError('assignmentName') ? styles.hasError : ''
                    }`}
                  >
                    <input
                      type="text"
                      name="assignmentName"
                      value={values.assignmentName}
                      onChange={handleChange}
                      placeholder="Assignment Name"
                      className={
                        showError('assignmentName') ? styles.error : ''
                      }
                      data-intercom-target="Crete Edit Name Field"
                    />
                    {showError('assignmentName') && (
                      <div className={styles.errorText}>
                        {errors.assignmentName}
                      </div>
                    )}
                  </div>

                  <div className={styles.formGroup}>
                    <textarea
                      name="description"
                      value={values.description}
                      onChange={handleChange}
                      placeholder="Optional Description..."
                    />
                  </div>

                  <div className={styles.dropdownContainer}>
                    <div
                      className={`${styles.dropdown} ${
                        showError('rubric') ? styles.hasError : ''
                      }`}
                    >
                      <select
                        name="rubric"
                        value={values.rubric || ''}
                        onChange={handleChange}
                        className={showError('rubric') ? styles.error : ''}
                        disabled={inNewCopyMode}
                      >
                        <option value="" disabled>
                          Rubric
                        </option>
                        {VALUES_OPTIONS.map((option) => (
                          <option key={option.key} value={option.key}>
                            {option.text}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div
                      className={`${styles.dropdown} ${
                        showError('pageType') ? styles.hasError : ''
                      }`}
                    >
                      {values.pageType === 'custom' ? (
                        <div className={styles.customInputWrapper}>
                          <input
                            type="number"
                            name="pageCount"
                            value={values.pageCount}
                            onChange={handleChange}
                            min="1"
                            placeholder="Enter"
                            className={`${styles.customInput} ${
                              showError('pageCount') ? styles.error : ''
                            }`}
                            disabled={
                              (isFromTemplate &&
                                mode !== AssignmentDialogModes.COPY) ||
                              inNewCopyMode
                            }
                            onBlur={(e) => {
                              if (!e.target.value || e.target.value < 1) {
                                setFieldValue('pageType', '');
                                setFieldValue('pageCount', '');
                              }
                            }}
                          />
                          <span className={styles.pagesText}>Pages</span>
                        </div>
                      ) : (
                        <select
                          name="pageType"
                          value={values.pageType}
                          onChange={(e) => {
                            const value = e.target.value;
                            setFieldValue('pageType', value);
                            if (value !== 'custom') {
                              setFieldValue('pageCount', value);
                            }
                          }}
                          className={showError('pageType') ? styles.error : ''}
                          disabled={
                            (isFromTemplate &&
                              mode !== AssignmentDialogModes.COPY) ||
                            inNewCopyMode
                          }
                        >
                          <option value="" disabled>
                            Pages
                          </option>
                          <option value="1">1 Page</option>
                          <option value="2">2 Pages</option>
                          <option value="3">3 Pages</option>
                          <option value="4">4 Pages</option>
                          <option value="custom">Custom</option>
                        </select>
                      )}
                    </div>
                    {values.rubric === AssignmentTaskValueTypes.CUSTOM && (
                      <div className={styles.formGroup}>
                        <input
                          type="number"
                          name="maxValue"
                          min="1"
                          value={values.maxValue}
                          onChange={handleChange}
                          placeholder="Enter max grade value (default 100)"
                        />
                      </div>
                    )}
                  </div>

                  <div className={styles.standardsContainer}>
                    {assignmentStandards.map((standard, index) => (
                      <button
                        key={standard.id || index}
                        type="button"
                        className={styles.standardTag}
                        disabled={inNewCopyMode}
                      >
                        <span className={styles.standardCode}>
                          {standard?.code}
                        </span>
                        {!(
                          isFromTemplate && mode !== AssignmentDialogModes.COPY
                        ) &&
                          !inNewCopyMode && (
                            <div
                              className={styles.closeButton}
                              onClick={(e) => {
                                e.stopPropagation();
                                const updatedTasks = values.tasks.map(
                                  (task) => ({
                                    ...task,
                                    standards: task.standards.filter(
                                      (s) => s.id !== standard?.id
                                    )
                                  })
                                );
                                setFieldValue('tasks', updatedTasks);
                              }}
                            >
                              <X size={14} />
                            </div>
                          )}
                      </button>
                    ))}
                    <button
                      type="button"
                      className={styles.addStandardBtn}
                      onClick={openStandardDialog}
                      disabled={
                        (isFromTemplate &&
                          mode !== AssignmentDialogModes.COPY) ||
                        inNewCopyMode
                      }
                      data-intercom-target="Create Edit Standards Button"
                    >
                      <Plus size={14} />
                      ADD STANDARD
                    </button>
                  </div>

                  {isMathAssignment &&
                    values.attachments.length > 0 &&
                    every(attachments, 'uploaded') &&
                    values.assignmentName !== '' &&
                    assignmentStandards.length === 0 && (
                      <Typography
                        type="H-TEXT-2"
                        color="red"
                        className={styles.standardError}
                      >
                        Please add at least one standard
                      </Typography>
                    )}

                  <div className={styles.advancedSettings}>
                    <button
                      type="button"
                      className={styles.header}
                      onClick={() => setShowAdvanced(!showAdvanced)}
                    >
                      <Typography variant="H-TEXT-3">
                        Advanced Settings
                      </Typography>
                      <ChevronDown
                        size={16}
                        style={{
                          transform: showAdvanced ? 'rotate(180deg)' : 'none',
                          transition: 'transform 0.2s'
                        }}
                      />
                    </button>

                    {showAdvanced && (
                      <div className={styles.content}>
                        <div className={styles.tabs}>
                          <button
                            type="button"
                            className={
                              activeSettingsTab === 'attachments'
                                ? styles.active
                                : ''
                            }
                            onClick={() => setActiveSettingsTab('attachments')}
                          >
                            {isMathAssignment ? 'Attachment' : 'Attachments'}
                          </button>
                        </div>

                        {activeSettingsTab === 'attachments' && (
                          <>
                            {!inNewCopyMode && (
                              <>
                                <Typography
                                  variant="B-TEXT-2"
                                  color={colors.blue}
                                >
                                  {attachmentsText}
                                </Typography>

                                <div
                                  className={`${styles.uploadArea} ${
                                    errors.attachments && isMathAssignment
                                      ? styles.attachmentError
                                      : ''
                                  }`}
                                  onClick={openFileDialog}
                                >
                                  <div className={styles.uploadButton}>
                                    <UploadIcon />
                                    <Typography
                                      variant="B-TEXT-2"
                                      color={colors.blue}
                                    >
                                      ADD EXEMPLAR
                                    </Typography>
                                  </div>
                                </div>
                              </>
                            )}

                            <AttachmentList
                              attachments={attachments}
                              onRemove={onRemove}
                              disableRemove={inNewCopyMode}
                              enableFileDownload={true}
                            />
                            {errors.attachments && isMathAssignment && (
                              <Typography
                                variant="B-TEXT-4"
                                color={colors.pink1}
                                className={styles.attachmentTextError}
                              >
                                {errors.attachments}
                              </Typography>
                            )}
                          </>
                        )}

                        {activeSettingsTab === 'ai' && (
                          <div className={styles.aiSettings}>
                            {/* AI settings content can be added here */}
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {isSubmitting && (
                    <div className={styles.loadingOverlay}>
                      <div className={styles.spinner} />
                    </div>
                  )}

                  {isStandardDialogOpened && (
                    <StandardDialogV2
                      selectedItems={assignmentStandards}
                      onClose={closeStandardDialog}
                      onSave={({ selectedStandards }) =>
                        onStandardsSelected({
                          selectedStandards,
                          formikTasks: values.tasks,
                          setFieldValue
                        })
                      }
                      isMathAssignment={isMathAssignment}
                    />
                  )}
                  {isAssignmentDialogOpened && (
                    <Dialog
                      open={true}
                      alignTop={true}
                      onClose={() => setIsAssignmentDialogOpened(false)}
                      /* We toggle the class here based on the state */
                      classes={{
                        paper: cx(styles.assignmentCreateDialog, {
                          [styles.wideDialog]: isWide
                        })
                      }}
                    >
                      {ELA_INSIGHTS_ENABLED ? (
                        <TeacherAssignmentCreateV2 onWidthChange={setIsWide} />
                      ) : (
                        <TeacherAssignmentCreate onWidthChange={setIsWide} />
                      )}
                    </Dialog>
                  )}
                </div>
              </>
            )}
          </AttachmentDropzone>
        );
      }}
    </Formik>
  );
};

export default StepperAssignmentCreateForm;
